//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { useSelector } from 'react-redux';

import {
  useAdobeOAuthSettingsQuery,
  useAdobeWebServiceSettingsQuery,
  useGoogleAnalytics4SettingsQuery,
  useVibePixelSettingsQuery,
} from '@account-settings/accountSettingsQueries';
import { getIsBvPixelEnabled } from '@insights/redux/selectors/getIsBvPixelEnabled';
import { EcommerceProvider } from '@models/MetricsGatewayApiModels';
import { useGetInsightsMetadataQuery } from '@reporting/queries/reportingQueries';
import { isDefined } from '@utils/filterPredicates';
import {
  OnboardingStepInfo,
  OnboardingStepStatus,
} from '@welcome-page/constants/OnboardingConstants';

export function useOnboardingAnalyticsStepInfo(): OnboardingStepInfo {
  // TODO PD-297299: Cleanup after GA
  const isBvPixelEnabled = useSelector(getIsBvPixelEnabled);

  const {
    data: isGA4Connected,
    isError: isGA4Error,
    isPending: isGA4Loading,
  } = useGoogleAnalytics4SettingsQuery((data) =>
    isDefined(data.currentPropertyId),
  );

  const {
    data: isAdobeWSConnected,
    isError: isAdobeWSError,
    isPending: isAdobeWSLoading,
  } = useAdobeWebServiceSettingsQuery((data) => data.isConnected);

  const {
    data: isAdobeOAuthConnected,
    isError: isAdobeOAuthError,
    isPending: isAdobeOAuthLoading,
  } = useAdobeOAuthSettingsQuery((data) => data.isConnected);

  const {
    data: isVibePixelConnected,
    isError: isVibePixelError,
    isPending: isVibePixelLoading,
  } = useVibePixelSettingsQuery((data) => data.isActive);

  const {
    data: metricsMetadata,
    isError: isMetricsMetadataError,
    isPending: isMetricsMetadataLoading,
  } = useGetInsightsMetadataQuery();

  return {
    status:
      (isBvPixelEnabled &&
        !!(metricsMetadata?.ecommerceProvider === EcommerceProvider.BvPixel)) ||
      !!isGA4Connected ||
      !!isAdobeWSConnected ||
      !!isAdobeOAuthConnected ||
      !!isVibePixelConnected
        ? OnboardingStepStatus.COMPLETE
        : OnboardingStepStatus.INCOMPLETE,
    isError:
      isMetricsMetadataError ||
      isGA4Error ||
      isAdobeWSError ||
      isAdobeOAuthError ||
      isVibePixelError,
    isPending:
      isMetricsMetadataLoading ||
      isGA4Loading ||
      isAdobeWSLoading ||
      isAdobeOAuthLoading ||
      isVibePixelLoading,
  };
}
